// 手机将明细按记录展示，保留原行事件；有交互表头或复杂行时保留可滑动表格，避免丢失排序、勾选和操作。
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { TEAMS } from "../data/constants";
import { fmtMoney, fmtMoneyShort, projectCollected } from "../lib/appUtils";
import Avatar from "../components/Avatar";
import { Input } from "../components/ui/input";
import { Archive, Search, CheckCircle2, Wallet, DollarSign, FileDown } from "lucide-react";
import { fmtD } from "../lib/date";
import { initTeamFilter, teamFilterKey, isTeamManager } from "../lib/teamScope";

// 保修分组：完工日起 12 个月保修期
const WARRANTY_MONTHS = 12;

/**
 * 取项目的「实际完工日期」——完工项目页展示与保修计算的唯一取值口径。
 *
 * 需求来源：FBd857097d6a（销售副总 Cathy 2026-10-20 反馈「请问下保修项目怎么修改时间」，
 * 截图里红框圈住「完工日期」「保修」两列，17 个项目全是「—」）。
 *
 * 为什么要有三级回退（而不是只读 completedDate）：
 *   ① completedDate —— 首选的权威字段。2026-10-21 前，POST /projects/{id}/advance
 *      （前端「完成本阶段 / 竣工」按钮走的接口）从不写它，导致生产库 phaseIndex >= 7
 *      的 17 个项目该字段全为 null，页面只能显示「—」。后端已修（advance 与 PATCH 现在
 *      都会补写 completedDate），但**历史数据不会自己长出来**，必须靠下面两级兜底。
 *   ② completedAt —— UTC 完整时间戳，同一次修复里一并写入的备份口径。
 *   ③ phaseHistory 里最后一条「流转到 7（完工）」记录的 date —— 历史数据唯一可靠的痕迹。
 *      它与后端 _completed_date_of()（server.py，创建已完工客户时用）的回退链一致，
 *      两处口径统一，避免同一项目在两个页面显示不同的完工日期。
 *   全部取不到才返回 null（前端显示「—」、保修判 unknown），不做臆造。
 *
 * @param {object} p 项目对象（来自 GET /projects）
 * @returns {string|null} YYYY-MM-DD 格式的完工日期；无任何可用来源时返回 null
 */
const completedDateOf = (p) => {
  const direct = p.completedDate || (p.completedAt ? String(p.completedAt).slice(0, 10) : "");
  if (direct) return direct;
  // phaseHistory 里可能有多次进入完工阶段的记录（撤销重推 / 阶段校准），取最后一条
  const hit = (p.phaseHistory || []).filter((h) => h && Number(h.to) >= 7 && h.date);
  return hit.length ? String(hit[hit.length - 1].date).slice(0, 10) : null;
};

const warrantyInfo = (p) => {
  const d = completedDateOf(p);
  const start = d ? new Date(`${d}T00:00:00`) : null;
  if (!start || isNaN(start)) return { status: "unknown", monthsLeft: null, endDate: null };
  const end = new Date(start);
  end.setMonth(end.getMonth() + WARRANTY_MONTHS);
  const active = new Date() < end;
  return { status: active ? "active" : "expired", monthsLeft: active ? Math.max(1, Math.round((end - new Date()) / (30 * 86400000))) : 0, endDate: end };
};

export default function CompletedArchive() {
  const { lang, user, allProjects: visibleProjects, avatarFor, api } = useApp();
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [teamFilter, setTeamFilter] = useState(() => initTeamFilter("archive", user));
  useEffect(() => { localStorage.setItem(teamFilterKey("archive", user), teamFilter); }, [teamFilter, user]);
  const [checked, setChecked] = useState([]);
  const [zipping, setZipping] = useState(false);
  const [warrantyFilter, setWarrantyFilter] = useState("all"); // all | active | expired
  const canSeeMoney = user.role === "admin" || user.role === "finance" || user.role === "ceo";
  const canExport = user.role === "admin" || user.role === "finance" || user.role === "ceo";

  const toggle = (id) => setChecked((c) => c.includes(id) ? c.filter((x) => x !== id) : [...c, id]);

  const batchExport = async () => {
    if (checked.length === 0) return;
    setZipping(true);
    try {
      const res = await api.post("/projects/settlement-zip", { ids: checked, lang }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = "settlements.zip"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { alert(lang === "zh" ? "批量导出失败" : "Batch export failed"); }
    setZipping(false);
  };

  const downloadSettlement = async (e, pid) => {
    e.stopPropagation();
    try {
      const res = await api.get(`/projects/${pid}/settlement-pdf`, { params: { lang }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `settlement_${pid}.pdf`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { alert(lang === "zh" ? "结算单生成失败" : "Settlement PDF failed"); }
  };

  const baseCompleted = useMemo(() => {
    let list = visibleProjects.filter((p) => p.phaseIndex >= 7);
    if (teamFilter !== "all") list = list.filter((p) => p.team === teamFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((p) => p.address.toLowerCase().includes(q) || (p.clientName || "").toLowerCase().includes(q) || (p.sales || "").toLowerCase().includes(q));
    }
    // 排序口径与展示口径必须同源（completedDateOf 含历史数据回退），
    // 否则老项目显示有日期、排序却按空值沉底，列表顺序会与日期列看起来矛盾。
    return list.sort((a, b) => (completedDateOf(b) || "").localeCompare(completedDateOf(a) || ""));
  }, [visibleProjects, teamFilter, query]);

  // 保修分组：完工日起 12 个月保修期；已过保可作老带新线索
  const completed = useMemo(
    () => (warrantyFilter === "all" ? baseCompleted : baseCompleted.filter((p) => warrantyInfo(p).status === warrantyFilter)),
    [baseCompleted, warrantyFilter]
  );
  const warrantyCounts = useMemo(() => ({
    active: baseCompleted.filter((p) => warrantyInfo(p).status === "active").length,
    expired: baseCompleted.filter((p) => warrantyInfo(p).status === "expired").length,
  }), [baseCompleted]);

  const totalContract = completed.reduce((s, p) => s + (p.contractAmount || 0), 0);
  const totalCollected = completed.reduce((s, p) => s + projectCollected(p), 0);
  const totalRemaining = Math.max(0, totalContract - totalCollected);

  const Stat = ({ icon: Icon, label, value, tint }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between"><p className="text-sm text-slate-500 font-medium">{label}</p>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: tint + "22" }}><Icon className="h-5 w-5" style={{ color: tint }} /></div></div>
      <p className="text-2xl font-extrabold text-slate-900 mt-3">{value}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Archive className="h-6 w-6 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{lang === "zh" ? "已完成项目归档" : "Completed Projects Archive"}</h1>
            <p className="text-slate-500 text-sm">{canExport ? (lang === "zh" ? "回看历史项目与最终结算" : "Historical projects & final settlement") : (lang === "zh" ? "回看历史已完工项目" : "Historical completed projects")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canExport && checked.length > 0 && (
            <button data-testid="batch-export-btn" onClick={batchExport} disabled={zipping}
              className="inline-flex items-center gap-1 text-sm text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50 rounded-lg px-3 py-2 transition-colors">
              <FileDown className="h-4 w-4" />{zipping ? (lang === "zh" ? "打包中…" : "Zipping…") : (lang === "zh" ? `批量导出结算单 (${checked.length})` : `Export (${checked.length})`)}
            </button>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={lang === "zh" ? "搜索：地址/客户/销售" : "Search: address / client / sales"} className="pl-9 w-72" data-testid="archive-search" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={CheckCircle2} label={lang === "zh" ? "已完成项目" : "Completed"} value={completed.length} tint="#059669" />
        {canSeeMoney && <Stat icon={DollarSign} label={lang === "zh" ? "合同总额" : "Total Contract"} value={fmtMoneyShort(totalContract)} tint="#0f172a" />}
        {canSeeMoney && <Stat icon={Wallet} label={lang === "zh" ? "累计已收" : "Total Collected"} value={fmtMoneyShort(totalCollected)} tint="#16a34a" />}
        {canSeeMoney && <Stat icon={Wallet} label={lang === "zh" ? "结算尾差" : "Remaining"} value={fmtMoneyShort(totalRemaining)} tint="#d97706" />}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 w-fit" data-testid="archive-team-filter">
          {[{ id: "all", label: lang === "zh" ? "全部小组" : "All Teams" }, ...TEAMS.map((tm) => ({ id: tm.id, label: `${lang === "zh" ? tm.zh : tm.name}${!isTeamManager(user) && tm.id === user.team ? (lang === "zh" ? "（本团队）" : " (mine)") : ""}` }))].map((tm) => (
            <button key={tm.id} data-testid={`archive-team-${tm.id}`} onClick={() => setTeamFilter(tm.id)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${teamFilter === tm.id ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}>{tm.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 w-fit" data-testid="archive-warranty-filter">
          {[
            { id: "all", label: lang === "zh" ? "全部保修状态" : "All warranty" },
            { id: "active", label: `${lang === "zh" ? "保修中" : "In warranty"} (${warrantyCounts.active})` },
            { id: "expired", label: `${lang === "zh" ? "已过保" : "Expired"} (${warrantyCounts.expired})` },
          ].map((w) => (
            <button key={w.id} data-testid={`archive-warranty-${w.id}`} onClick={() => setWarrantyFilter(w.id)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${warrantyFilter === w.id ? "bg-emerald-600 text-white" : "text-slate-500 hover:text-slate-800"}`}>{w.label}</button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="archive-table">
        <div className="overflow-x-auto">
          <ResponsiveTable className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500"><tr>
              {canExport && <th className="px-4 py-3 w-10"><input type="checkbox" data-testid="select-all-checkbox" checked={completed.length > 0 && checked.length === completed.length} onChange={(e) => setChecked(e.target.checked ? completed.map((p) => p.id) : [])} /></th>}
              <th className="text-left font-medium px-5 py-3">{lang === "zh" ? "项目" : "Project"}</th>
              <th className="text-left font-medium px-4 py-3">{lang === "zh" ? "小组" : "Team"}</th>
              <th className="text-left font-medium px-4 py-3">{lang === "zh" ? "销售" : "Sales"}</th>
              <th className="text-left font-medium px-4 py-3">{lang === "zh" ? "完工日期" : "Completed"}</th>
              <th className="text-left font-medium px-4 py-3">{lang === "zh" ? "保修" : "Warranty"}</th>
              {canSeeMoney && <th className="text-right font-medium px-4 py-3">{lang === "zh" ? "合同额" : "Contract"}</th>}
              {canSeeMoney && <th className="text-right font-medium px-4 py-3">{lang === "zh" ? "已收" : "Collected"}</th>}
              {canSeeMoney && <th className="text-right font-medium px-4 py-3">{lang === "zh" ? "结算" : "Settled"}</th>}
              {canExport && <th className="text-right font-medium px-4 py-3" data-testid="settlement-col-header">{lang === "zh" ? "结算单" : "Statement"}</th>}
            </tr></thead>
            <tbody>
              {completed.length === 0 && <tr><td colSpan={(canSeeMoney ? 8 : 5) + (canExport ? 2 : 0)} className="text-center text-slate-300 py-10">{lang === "zh" ? "暂无已完成项目" : "No completed projects yet"}</td></tr>}
              {completed.map((p) => {
                const col = projectCollected(p);
                const settled = (p.contractAmount || 0) - col <= 0;
                return (
                  <tr key={p.id} data-testid={`archive-row-${p.id}`} onClick={() => nav(`/project/${p.id}`)} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer">
                    {canExport && <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" data-testid={`row-checkbox-${p.id}`} checked={checked.includes(p.id)} onChange={() => toggle(p.id)} /></td>}
                    <td className="px-5 py-3"><p className="font-medium text-slate-800 max-w-[240px] truncate">{p.address}</p><p className="text-[11px] text-slate-400">{p.clientName} · {p.type}</p></td>
                    <td className="px-4 py-3 text-slate-500">{p.teamName}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-2 text-slate-600"><Avatar src={avatarFor(p.sales)} name={p.sales} size={24} />{p.sales || "—"}</span></td>
                    <td className="px-4 py-3 text-slate-500" data-testid={`archive-completed-date-${p.id}`}>{fmtD(completedDateOf(p)) || "—"}</td>
                    <td className="px-4 py-3">
                      {(() => {
                        const w = warrantyInfo(p);
                        return (
                          <span data-testid={`archive-warranty-badge-${p.id}`} className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${w.status === "active" ? "bg-emerald-100 text-emerald-700" : w.status === "expired" ? "bg-slate-100 text-slate-500" : "bg-slate-100 text-slate-400"}`}>
                            {w.status === "active" ? (lang === "zh" ? `保修中 · 剩 ${w.monthsLeft} 月` : `In warranty · ${w.monthsLeft}mo`) : w.status === "expired" ? (lang === "zh" ? "已过保" : "Expired") : "—"}
                          </span>
                        );
                      })()}
                    </td>
                    {canSeeMoney && <td className="px-4 py-3 text-right font-medium text-slate-900">{fmtMoney(p.contractAmount)}</td>}
                    {canSeeMoney && <td className="px-4 py-3 text-right text-green-600">{fmtMoney(col)}</td>}
                    {canSeeMoney && <td className="px-4 py-3 text-right"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${settled ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{settled ? (lang === "zh" ? "已结清" : "Settled") : (lang === "zh" ? "有尾款" : "Balance")}</span></td>}
                    {canExport && <td className="px-4 py-3 text-right">
                      <button data-testid={`settlement-pdf-btn-${p.id}`} onClick={(e) => downloadSettlement(e, p.id)} className="inline-flex items-center gap-1 text-xs text-slate-600 border border-slate-200 hover:border-brand-300 hover:text-brand-500 rounded-md px-2 py-1 transition-colors">
                        <FileDown className="h-3.5 w-3.5" />PDF
                      </button>
                    </td>}
                  </tr>
                );
              })}
            </tbody>
          </ResponsiveTable>
        </div>
      </div>

    </div>
  );
}
